import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-border">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <img src="/konnect-logo.png" alt="Konnect" className="h-8 w-auto object-contain" />
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-gray-medium hover:text-text-primary transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="pt-20 sm:pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto prose prose-gray" data-testid="terms-of-service-page">
          <h1 className="text-2xl sm:text-4xl font-heading font-bold text-text-primary mb-2 sm:mb-3">Terms of Service</h1>
          <p className="text-gray-medium mb-8">Last Updated: April 1, 2026</p>

          <p className="text-text-primary leading-relaxed">
            These Terms of Service ("Terms") govern your access to and use of the Konnect platform ("Platform," "Service") 
            operated by <strong>Kaharian Integrated Business Solutions</strong> ("Company," "we," "us," or "our"). 
            By creating an account or using Konnect, you agree to be bound by these Terms.
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">1. Acceptance of Terms</h2>
          <p className="text-text-primary leading-relaxed">
            By accessing or using Konnect, you confirm that you are at least 18 years old and have the legal authority 
            to enter into these Terms on behalf of yourself or the organization you represent. If you do not agree with 
            these Terms, you must not use the Platform.
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">2. Description of Service</h2>
          <p className="text-text-primary leading-relaxed">
            Konnect is an AI-powered inbound marketing automation platform that provides:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-text-primary">
            <li>Social media management and automated posting</li>
            <li>AI-generated marketing content (blog posts, emails, ad copy, social captions)</li>
            <li>Email marketing campaigns and automation</li>
            <li>Website performance monitoring and management</li>
            <li>Online reputation and review management</li>
            <li>Search engine optimization (SEO) tools</li>
            <li>ADA compliance scanning and reporting</li>
            <li>Paid advertising management</li>
            <li>Analytics and reporting dashboards</li>
          </ul>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">3. Account Registration</h2>
          <ul className="list-disc pl-6 space-y-2 text-text-primary">
            <li>You must provide accurate, current, and complete information during registration.</li>
            <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
            <li>You are responsible for all activities that occur under your account.</li>
            <li>You must notify us immediately of any unauthorized access to your account.</li>
            <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
          </ul>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">4. Subscription Plans & Billing</h2>
          <h3 className="text-lg font-heading font-medium text-text-primary mt-6 mb-3">4.1 Plans</h3>
          <p className="text-text-primary leading-relaxed">
            Konnect offers tiered subscription plans (Starter, Growth, Enterprise) with varying features and limits. 
            Plan details and pricing are available on our website and may be updated with prior notice.
          </p>
          <h3 className="text-lg font-heading font-medium text-text-primary mt-6 mb-3">4.2 Billing</h3>
          <ul className="list-disc pl-6 space-y-2 text-text-primary">
            <li>Subscriptions are billed monthly or annually, depending on the plan selected.</li>
            <li>All payments are processed securely through Stripe. By subscribing, you authorize recurring charges.</li>
            <li>Prices are in USD unless otherwise stated by your local country operator.</li>
            <li>You may cancel your subscription at any time. Cancellation takes effect at the end of the current billing period.</li>
          </ul>
          <h3 className="text-lg font-heading font-medium text-text-primary mt-6 mb-3">4.3 Refunds</h3>
          <p className="text-text-primary leading-relaxed">
            We offer a 7-day free trial with 100 credits included for new accounts. After the trial, payments are non-refundable except 
            where required by applicable law. If you experience technical issues that prevent you from using the 
            Platform, contact our support team for resolution.
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">5. Acceptable Use</h2>
          <p className="text-text-primary leading-relaxed mb-3">You agree NOT to:</p>
          <ul className="list-disc pl-6 space-y-2 text-text-primary">
            <li>Use the Platform for any unlawful purpose or in violation of any applicable regulations.</li>
            <li>Send spam, unsolicited messages, or deceptive content through the Platform.</li>
            <li>Upload malicious code, viruses, or any content that could harm the Platform or its users.</li>
            <li>Attempt to gain unauthorized access to other users' accounts or our systems.</li>
            <li>Use AI-generated content for defamation, harassment, misinformation, or hate speech.</li>
            <li>Resell, redistribute, or sublicense the Platform without written authorization.</li>
            <li>Reverse-engineer, decompile, or disassemble any part of the Platform.</li>
            <li>Exceed the usage limits of your subscription plan through automated means.</li>
          </ul>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">6. AI-Generated Content</h2>
          <ul className="list-disc pl-6 space-y-2 text-text-primary">
            <li>Content generated by AI through Konnect is provided as-is. You are responsible for reviewing, editing, and approving all AI-generated content before publication.</li>
            <li>We do not guarantee the accuracy, completeness, or suitability of AI-generated content for any specific purpose.</li>
            <li>You retain ownership of the content you create or generate using the Platform.</li>
            <li>We are not liable for any consequences arising from the publication of AI-generated content.</li>
          </ul>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">7. Intellectual Property</h2>
          <ul className="list-disc pl-6 space-y-2 text-text-primary">
            <li>Konnect, its logo, design, and underlying technology are the intellectual property of Kaharian Integrated Business Solutions.</li>
            <li>Your content remains your property. By using the Platform, you grant us a limited license to process your content solely to provide the Service.</li>
            <li>You may not use our trademarks, logos, or branding without written permission.</li>
          </ul>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">8. Country Operators & Agencies</h2>
          <p className="text-text-primary leading-relaxed">
            Konnect operates in various countries through authorized country operators ("Operators"). Operators are 
            independent entities authorized to market, sell, and support Konnect within their designated territory.
          </p>
          <ul className="list-disc pl-6 space-y-2 text-text-primary">
            <li>If you were onboarded by a country operator, your primary support relationship is with that operator.</li>
            <li>Operators may offer localized pricing, terms, or additional services within their territory.</li>
            <li>Kaharian Integrated Business Solutions retains platform-level administrative authority globally.</li>
            <li>Operators have access to client data only within their assigned territory and in compliance with our Privacy Policy.</li>
          </ul>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">9. Third-Party Integrations</h2>
          <p className="text-text-primary leading-relaxed">
            Konnect integrates with third-party services (social media platforms, email providers, analytics tools, 
            payment processors). Your use of these integrations is subject to the respective third-party terms and policies. 
            We are not responsible for the availability, accuracy, or performance of third-party services.
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">10. Limitation of Liability</h2>
          <p className="text-text-primary leading-relaxed">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, KAHARIAN INTEGRATED BUSINESS SOLUTIONS SHALL NOT BE LIABLE FOR 
            ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF 
            PROFITS, DATA, BUSINESS OPPORTUNITIES, OR GOODWILL, ARISING FROM YOUR USE OF OR INABILITY TO USE THE PLATFORM.
          </p>
          <p className="text-text-primary leading-relaxed mt-3">
            Our total liability for any claim arising from these Terms shall not exceed the amount you paid 
            to us in the twelve (12) months preceding the claim.
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">11. Indemnification</h2>
          <p className="text-text-primary leading-relaxed">
            You agree to indemnify, defend, and hold harmless Kaharian Integrated Business Solutions, its officers, 
            directors, employees, agents, and authorized operators from any claims, damages, losses, or expenses 
            (including legal fees) arising from your use of the Platform, violation of these Terms, or infringement 
            of any third-party rights.
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">12. Service Availability</h2>
          <p className="text-text-primary leading-relaxed">
            We strive for 99.9% uptime but do not guarantee uninterrupted service. We may perform scheduled 
            maintenance with advance notice. We are not liable for service interruptions caused by factors 
            beyond our reasonable control (force majeure, internet outages, third-party service failures).
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">13. Termination</h2>
          <ul className="list-disc pl-6 space-y-2 text-text-primary">
            <li>You may terminate your account at any time through the Platform settings or by contacting support.</li>
            <li>We may suspend or terminate your account for violations of these Terms, non-payment, or abusive behavior.</li>
            <li>Upon termination, your access to the Platform ceases. You may request export of your data within 30 days of termination.</li>
            <li>Provisions that by their nature should survive termination (liability, indemnification, IP) will remain in effect.</li>
          </ul>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">14. Governing Law</h2>
          <p className="text-text-primary leading-relaxed">
            These Terms are governed by and construed in accordance with the laws of the jurisdiction in which 
            Kaharian Integrated Business Solutions is incorporated, without regard to conflict of law principles. 
            Any disputes arising under these Terms shall be resolved through binding arbitration or in the courts 
            of competent jurisdiction.
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">15. Modifications</h2>
          <p className="text-text-primary leading-relaxed">
            We reserve the right to modify these Terms at any time. Material changes will be communicated via 
            email or through the Platform at least 30 days in advance. Your continued use of Konnect after 
            modifications constitutes acceptance of the updated Terms.
          </p>

          <h2 className="text-xl font-heading font-semibold text-text-primary mt-8 mb-4">16. Contact</h2>
          <p className="text-text-primary leading-relaxed">
            For questions or concerns about these Terms:
          </p>
          <div className="bg-off-white rounded-xl p-6 mt-4">
            <p className="font-semibold text-text-primary">Kaharian Integrated Business Solutions</p>
            <p className="text-gray-medium mt-1">Email: <a href="mailto:legal@konnect.com" className="text-emerald-brand hover:underline">legal@konnect.com</a></p>
            <p className="text-gray-medium">Website: <a href="https://konnect.com" className="text-emerald-brand hover:underline">konnect.com</a></p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 bg-slate-dark border-t border-gray-700">
        <div className="max-w-4xl mx-auto text-center text-xs sm:text-sm text-gray-medium">
          <p>&copy; 2026 Konnect by Kaharian Integrated Business Solutions. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
